import { useEffect, useRef, useState } from "react";

/**
 * A score that counts up or down to its new value instead of jumping, so a tap is seen to
 * land. Screen readers get the final number once, not every step of the count.
 */
export function ScoreNumber({ value, className }: { value: number; className?: string }) {
  const [shown, setShown] = useState(value);
  // Where the count currently stands, so a tap mid-count carries on from there.
  const current = useRef(value);

  useEffect(() => {
    const from = current.current;
    if (from === value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      current.current = value;
      setShown(value);
      return;
    }
    const duration = Math.min(700, 200 + Math.abs(value - from) * 30);
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      current.current = Math.round(from + (value - from) * eased);
      setShown(current.current);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return (
    <span className={className}>
      <span aria-hidden="true">{shown}</span>
      <span className="visually-hidden" aria-live="polite">
        {value} points
      </span>
    </span>
  );
}
